/**
 * Staff Roster Service — Frontend API client
 * 
 * Interacts with /api/v1/staff-roster/* endpoints.
 */
import api from './api';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export interface ShiftType {
  name: string;
  code: string;
  start: string;
  end: string;
}

export interface CyclePattern {
  work_days: number;
  off_days: number;
}

export interface StaffRosterConfig {
  id: string;
  icao_code: string;
  name: string;
  shift_types: ShiftType[];
  cycle_pattern: CyclePattern;
  on_call_response_minutes: number;
  on_call_persons: number;
  dsr_override: number | null;
  headcount_override: number | null;
  holidays_per_person: number;
  training_days_per_person: number;
  absenteeism_pct: number;
  source: 'adosa' | 'manual';
  adosa_project_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}

export interface StaffRoster {
  id: string;
  config_id: string;
  roster_type: 'annual_generic' | 'monthly_specific';
  year: number;
  month: number | null;
  roster_data: string[][] | null;
  daily_coverage: number[] | null;
  employee_stats: Record<string, any>[] | null;
  months_summary: Record<string, any>[] | null;
  coverage_ok: boolean;
  headcount: number | null;
  dsr: number | null;
  cycle_work_days: number | null;
  cycle_off_days: number | null;
  generated_at: string;
}

export interface RosterSummary {
  id: string;
  roster_type: string;
  year: number;
  month: number | null;
  coverage_ok: boolean;
  headcount: number | null;
  dsr: number | null;
  generated_at: string;
}

export interface TrafficForecast {
  id: string;
  config_id: string;
  forecast_data: Record<string, any>[] | null;
  dacams_analysis_results: Record<string, any> | null;
  high_demand_days: Record<string, any>[] | null;
  file_name: string | null;
  analysis_status: string;
  uploaded_at: string;
}

export interface GuardAlert {
  id: string;
  config_id: string;
  alert_type: string;
  target_date: string;
  trigger_time: string | null;
  reason: string;
  details: Record<string, any> | null;
  source: string;
  severity: 'info' | 'warning' | 'critical';
  resolved: boolean;
  resolved_at: string | null;
  created_at: string;
}

export interface GuardEvaluation {
  config_id: string;
  target_date: string;
  weather_assessment: Record<string, any> | null;
  traffic_assessment: Record<string, any> | null;
  guard_needed: boolean;
  recommended_activation_time: string | null;
  alerts_generated: number;
  alerts: GuardAlert[];
}

// ─────────────────────────────────────────────
// Config CRUD
// ─────────────────────────────────────────────

export const staffRosterService = {
  // Configs
  async getConfigs(): Promise<StaffRosterConfig[]> {
    const res = await api.get('/staff-roster/configs');
    return res.data;
  },

  async getConfig(id: string): Promise<StaffRosterConfig> {
    const res = await api.get(`/staff-roster/configs/${id}`);
    return res.data;
  },

  async createConfig(data: Partial<StaffRosterConfig>): Promise<StaffRosterConfig> {
    const res = await api.post('/staff-roster/configs', data);
    return res.data;
  },

  async updateConfig(id: string, data: Partial<StaffRosterConfig>): Promise<StaffRosterConfig> {
    const res = await api.put(`/staff-roster/configs/${id}`, data);
    return res.data;
  },

  async deleteConfig(id: string): Promise<void> {
    await api.delete(`/staff-roster/configs/${id}`);
  },

  // ADOSA Import
  async importFromAdosa(configId: string, projectId: string): Promise<any> {
    const res = await api.post(`/staff-roster/configs/${configId}/import-adosa`, {
      project_id: projectId,
    });
    return res.data;
  },

  // Roster Generation
  async generateAnnualRoster(configId: string, year: number): Promise<StaffRoster> {
    const res = await api.post(`/staff-roster/configs/${configId}/generate/annual`, { year });
    return res.data;
  },

  async generateMonthlyRoster(configId: string, year: number, month: number): Promise<StaffRoster> {
    const res = await api.post(`/staff-roster/configs/${configId}/generate/monthly`, { year, month });
    return res.data;
  },

  async getRosters(configId: string): Promise<RosterSummary[]> {
    const res = await api.get(`/staff-roster/configs/${configId}/rosters`);
    return res.data;
  },

  async getRoster(id: string): Promise<StaffRoster> {
    const res = await api.get(`/staff-roster/rosters/${id}`);
    return res.data;
  },

  // Traffic Forecast
  async uploadTrafficForecast(configId: string, file: File): Promise<TrafficForecast> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(`/staff-roster/configs/${configId}/traffic-forecast`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  async analyzeTrafficForecast(forecastId: string): Promise<TrafficForecast> {
    const res = await api.post(`/staff-roster/forecasts/${forecastId}/analyze`);
    return res.data;
  },

  // Guard Evaluation
  async evaluateGuard(configId: string): Promise<GuardEvaluation> {
    const res = await api.post(`/staff-roster/configs/${configId}/evaluate-guard`);
    return res.data;
  },

  // Alerts
  async getAlerts(configId: string, resolved?: boolean): Promise<GuardAlert[]> {
    const params: any = {};
    if (resolved !== undefined) params.resolved = resolved;
    const res = await api.get(`/staff-roster/configs/${configId}/alerts`, { params });
    return res.data;
  },

  async resolveAlert(alertId: string): Promise<GuardAlert> {
    const res = await api.put(`/staff-roster/alerts/${alertId}/resolve`);
    return res.data;
  },
};
